/**
 * Node Normalization Utilities
 * Handles ID normalization for namespaced vs non-namespaced data
 */

import { getNodeId } from '@/features/projects/MultiProjectContext';

function normalizeIdValue(
  value: string,
  idsAreNamespaced: boolean,
  projectId: string | undefined
): string {
  if (idsAreNamespaced) {
    return value.includes(':') ? value : (projectId ? `${projectId}:${value}` : value);
  }
  return value.includes(':') ? getNodeId(value) : value;
}

function normalizeNodeShape(
  node: Record<string, unknown> | null | undefined,
  idsAreNamespaced: boolean,
  projectId: string | undefined
): Record<string, unknown> | null | undefined {
  if (!node || typeof node !== 'object') return node;

  return {
    ...node,
    id: typeof node.id === 'string' ? normalizeIdValue(node.id, idsAreNamespaced, projectId) : node.id,
    parentId:
      typeof node.parentId === 'string'
        ? normalizeIdValue(node.parentId, idsAreNamespaced, projectId)
        : node.parentId,
    dependencies: Array.isArray(node.dependencies)
      ? node.dependencies.map((dependencyId: string) =>
          typeof dependencyId === 'string'
            ? normalizeIdValue(dependencyId, idsAreNamespaced, projectId)
            : dependencyId
        )
      : node.dependencies,
  };
}

/**
 * Normalize operation IDs based on data namespace status
 */
export function normalizeOperationIds(
  operation: Record<string, unknown>,
  idsAreNamespaced: boolean,
  projectId: string | undefined
): Record<string, unknown> {
  const op = { ...operation };

  // Normalize nodeId
  if (op.nodeId && typeof op.nodeId === 'string') {
    op.nodeId = normalizeIdValue(op.nodeId, idsAreNamespaced, projectId);
  }

  if (op.deletedNodeId && typeof op.deletedNodeId === 'string') {
    op.deletedNodeId = normalizeIdValue(op.deletedNodeId, idsAreNamespaced, projectId);
  }

  if (Array.isArray(op.deletedNodeIds)) {
    op.deletedNodeIds = op.deletedNodeIds.map((id: string) => normalizeIdValue(id, idsAreNamespaced, projectId));
  }

  // Normalize parentId for all operations that carry it, including create
  if (typeof op.parentId === 'string') {
    op.parentId = normalizeIdValue(op.parentId, idsAreNamespaced, projectId);
  }

  // Normalize data payload IDs when present
  if (op.data && typeof op.data === 'object') {
    op.data = normalizeNodeShape(op.data as Record<string, unknown>, idsAreNamespaced, projectId);
  }

  // Normalize move operation
  if (op.type === 'move') {
    // Normalize targetParentId
    if (typeof op.targetParentId === 'string') {
      op.targetParentId = normalizeIdValue(op.targetParentId, idsAreNamespaced, projectId);
    }

    if (typeof op.sourceParentId === 'string') {
      op.sourceParentId = normalizeIdValue(op.sourceParentId, idsAreNamespaced, projectId);
    }

    // CRITICAL FIX: Normalize nodeIds array (authoritative sibling order from backend)
    if (Array.isArray(op.nodeIds)) {
      op.nodeIds = op.nodeIds.map((id: string) => normalizeIdValue(id, idsAreNamespaced, projectId));
    }

    if (Array.isArray(op.sourceNodeIds)) {
      op.sourceNodeIds = op.sourceNodeIds.map((id: string) => normalizeIdValue(id, idsAreNamespaced, projectId));
    }
  }

  // Normalize reorder payload (nodeIds and parentId)
  if (op.type === 'reorder') {
    if (Array.isArray(op.nodeIds)) {
      op.nodeIds = op.nodeIds.map((id: string) => normalizeIdValue(id, idsAreNamespaced, projectId));
    }
  }

  if ((op.type === 'batch-update' || op.type === 'delete-tree') && Array.isArray(op.updates)) {
    op.updates = (op.updates as Record<string, unknown>[]).map((update: Record<string, unknown>) => ({
      ...update,
      nodeId:
        typeof update?.nodeId === 'string'
          ? normalizeIdValue(update.nodeId, idsAreNamespaced, projectId)
          : update?.nodeId,
      data: normalizeNodeShape(update?.data as Record<string, unknown> | null | undefined, idsAreNamespaced, projectId),
    }));
  }

  if ((op.type === 'batch-update' || op.type === 'delete-tree')) {
    if (Array.isArray(op.nodeIds)) op.nodeIds = op.nodeIds.map((id: string) => normalizeIdValue(id, idsAreNamespaced, projectId));
    if (typeof op.sourceParentId === 'string') op.sourceParentId = normalizeIdValue(op.sourceParentId, idsAreNamespaced, projectId);
    if (Array.isArray(op.sourceNodeIds)) op.sourceNodeIds = op.sourceNodeIds.map((id: string) => normalizeIdValue(id, idsAreNamespaced, projectId));
  }

  if ((op.type === 'batch-update' || op.type === 'delete-tree') && Array.isArray(op.updatedNodes)) {
    op.updatedNodes = (op.updatedNodes as Record<string, unknown>[]).map((item: Record<string, unknown>) => ({
      ...item,
      nodeId:
        typeof item?.nodeId === 'string'
          ? normalizeIdValue(item.nodeId, idsAreNamespaced, projectId)
          : item?.nodeId,
      node: normalizeNodeShape(item?.node as Record<string, unknown> | null | undefined, idsAreNamespaced, projectId),
    }));
  }

  if (op.type === 'group-move' && Array.isArray(op.updatedNodes)) {
    op.updatedNodes = (op.updatedNodes as Record<string, unknown>[]).map((item: Record<string, unknown>) => {
      if (item && typeof item === 'object' && 'node' in item && 'nodeId' in item) {
        return {
          ...item,
          nodeId:
            typeof item?.nodeId === 'string'
              ? normalizeIdValue(item.nodeId, idsAreNamespaced, projectId)
              : item?.nodeId,
          node: normalizeNodeShape(item?.node as Record<string, unknown> | null | undefined, idsAreNamespaced, projectId),
        };
      }

      return normalizeNodeShape(item as Record<string, unknown>, idsAreNamespaced, projectId);
    });
  }

  // CRITICAL FIX: Normalize batch-delete nodeIds
  if (op.type === 'batch-delete' && Array.isArray(op.nodeIds)) {
    op.nodeIds = op.nodeIds.map((id: string) => normalizeIdValue(id, idsAreNamespaced, projectId));
  }

  return op;
}

/**
 * Check if nodes are using namespaced IDs
 */
export function checkIfNamespaced(nodes: Array<{ id?: string }>): boolean {
  return Array.isArray(nodes) && nodes.some(n => typeof n.id === 'string' && n.id.includes(':'));
}
